// NotificationsCenter — multi-channel dispatch inbox.
//
// Backend: services/notification_engine.py
//   • Each notification carries a list of channel deliveries
//     (in_app / email / sms / whatsapp) with their own lifecycle.
//   • This page shows the inbox + the delivery status chips per
//     channel + a one-click retry for failed channels + a tester for
//     admins to verify the engine end-to-end.
//
// Three tabs:
//   - Inbox       → user-visible notifications (unread + read).
//   - Delivery     → channel-level matrix (delivered / failed / pending counts).
//   - Test & Tools → admin-only send-test panel and channel selector.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Bell, BellOff, CheckCheck, RefreshCw, AlertCircle, AlertTriangle,
  Info, Mail, MessageSquare, Smartphone, Send, Beaker, Activity,
  CheckCircle2, XCircle, Clock, Loader2, ExternalLink,
} from 'lucide-react';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// ─── Channel & status icon/colour maps ───────────────────────────────────

const CHANNEL_META = {
  in_app:   { icon: Bell,          label: 'In-app',    color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  email:    { icon: Mail,          label: 'Email',     color: 'bg-blue-50 text-blue-700 border-blue-200' },
  sms:      { icon: Smartphone,    label: 'SMS',       color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  whatsapp: { icon: MessageSquare, label: 'WhatsApp',  color: 'bg-green-50 text-green-700 border-green-200' },
};

const STATUS_META = {
  delivered:    { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50',   spin: false, label: 'Delivered' },
  sent:         { icon: CheckCircle2, color: 'text-emerald-500 bg-emerald-50',   spin: false, label: 'Sent' },
  sending:      { icon: Loader2,      color: 'text-sky-600 bg-sky-50',           spin: true,  label: 'Sending' },
  queued:       { icon: Clock,        color: 'text-slate-500 bg-slate-50',       spin: false, label: 'Queued' },
  retrying:     { icon: RefreshCw,    color: 'text-amber-600 bg-amber-50',       spin: true,  label: 'Retrying' },
  failed:       { icon: AlertTriangle,color: 'text-orange-600 bg-orange-50',     spin: false, label: 'Failed' },
  failed_final: { icon: XCircle,      color: 'text-rose-600 bg-rose-50',         spin: false, label: 'Failed (final)' },
};

const DISPATCH_BADGE = {
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  partial:   'bg-amber-100 text-amber-800 border-amber-200',
  pending:   'bg-sky-100 text-sky-800 border-sky-200',
  failed:    'bg-rose-100 text-rose-800 border-rose-200',
};

const sevIcon = (sev) => {
  if (sev === 'critical') return <AlertCircle className="text-rose-600" size={18} />;
  if (sev === 'warning')  return <AlertTriangle className="text-amber-500" size={18} />;
  return <Info className="text-sky-500" size={18} />;
};


export default function NotificationsCenter() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/notifications?unread_only=${unreadOnly}&limit=200`);
      setList(r.data || []);
    } catch {
      toast.error('Failed to load notifications');
    }
  }, [unreadOnly]);

  const loadStats = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/notifications/dispatch-stats');
      setStats(r.data);
    } catch {/* non-blocking */}
  }, []);

  useEffect(() => {
    const h = setTimeout(() => { load(); loadStats(); }, 0);
    return () => clearTimeout(h);
  }, [load, loadStats]);

  const markRead = async (id) => {
    await axiosInstance.post(`/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await axiosInstance.post('/notifications/read-all');
    toast.success('All marked as read'); load();
  };
  const scan = async () => {
    setScanning(true);
    try {
      const r = await axiosInstance.post('/notifications/scan');
      toast.success(`Scan complete — ${r.data.created} new alert(s)`);
      load();
    } catch {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };
  const retryChannel = async (notifId, channel) => {
    try {
      await axiosInstance.post(`/notifications/${notifId}/retry?channel=${channel}`);
      toast.success(`${channel} retry triggered`);
      setTimeout(() => { load(); loadStats(); }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    }
  };

  return (
    <div
      data-testid="notifications-center"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6 space-y-5"
      dir="ltr"
    >
      <Header onScan={scan} scanning={scanning} onMarkAll={markAll} />

      <Tabs defaultValue="inbox" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inbox" data-testid="tab-inbox"><Bell size={14} className="mr-1.5" /> Inbox</TabsTrigger>
          <TabsTrigger value="delivery" data-testid="tab-delivery"><Activity size={14} className="mr-1.5" /> Delivery</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools"><Beaker size={14} className="mr-1.5" /> Tools</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
                data-testid="unread-only-toggle"
              />
              Show unread only
            </label>
            <span className="text-xs text-slate-500 ml-auto">Total: {list.length}</span>
          </div>

          {list.length === 0 ? (
            <div data-testid="inbox-empty" className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400 flex flex-col items-center gap-2">
              <BellOff size={48} className="text-slate-300" />
              No notifications
            </div>
          ) : (
            list.map((n) => (
              <NotificationCard
                key={n.id}
                n={n}
                onMarkRead={markRead}
                onRetry={retryChannel}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="delivery">
          <DeliveryMatrix stats={stats} />
        </TabsContent>

        <TabsContent value="tools">
          <TestEnginePanel onSent={() => { load(); loadStats(); }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ─── Header ──────────────────────────────────────────────────────────────


function Header({ onScan, scanning, onMarkAll }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-sm">
          <Bell size={20} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="page-title">
            Notifications
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Real-time dispatch across in-app, email, SMS and WhatsApp.
            Each delivery is tracked end-to-end with retries and an audit trail.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={onScan}
          disabled={scanning}
          data-testid="scan-btn"
        >
          <RefreshCw size={14} className={`mr-1.5 ${scanning ? 'animate-spin' : ''}`} />
          Scan for alerts
        </Button>
        <Button
          variant="outline"
          onClick={onMarkAll}
          data-testid="mark-all-btn"
        >
          <CheckCheck size={14} className="mr-1.5" /> Mark all read
        </Button>
      </div>
    </div>
  );
}


// ─── Notification card ───────────────────────────────────────────────────


function NotificationCard({ n, onMarkRead, onRetry }) {
  const channels = n.channels || [];
  const dispatch = n.dispatch_status || 'delivered';
  const dispatchClass = DISPATCH_BADGE[dispatch] || DISPATCH_BADGE.delivered;

  return (
    <div
      data-testid={`notification-${n.id}`}
      className={`bg-white rounded-xl border shadow-sm p-4 flex items-start gap-3 transition ${
        !n.read ? 'border-indigo-200 ring-1 ring-indigo-100' : 'border-slate-200 opacity-90'
      }`}
    >
      <div className="mt-1">{sevIcon(n.severity)}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-sm text-slate-900">{n.title || (n.message || '').slice(0, 80)}</h3>
          <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-600">
            {(n.kind || 'system').replace(/_/g, ' ')}
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border capitalize ${dispatchClass}`}>
            {dispatch}
          </span>
        </div>
        <p className="text-sm text-slate-600 mt-1">{n.message}</p>

        {channels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {channels.map((c) => (
              <ChannelChip
                key={c.name}
                ch={c}
                notifId={n.id}
                onRetry={onRetry}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
          <span>{new Date(n.created_at).toLocaleString()}</span>
          {n.link && (
            <a
              href={n.link}
              className="text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center gap-0.5"
            >
              View <ExternalLink size={11} />
            </a>
          )}
        </div>
      </div>
      {!n.read && (
        <button
          onClick={() => onMarkRead(n.id)}
          className="text-xs text-slate-500 hover:text-slate-700 p-1"
          data-testid={`mark-read-${n.id}`}
          title="Mark as read"
        >
          <CheckCheck size={16} />
        </button>
      )}
    </div>
  );
}


function ChannelChip({ ch, notifId, onRetry }) {
  const meta = CHANNEL_META[ch.name] || { icon: Bell, label: ch.name, color: 'bg-slate-50 text-slate-700 border-slate-200' };
  const statusMeta = STATUS_META[ch.status] || STATUS_META.queued;
  const Icon = meta.icon;
  const StatusIcon = statusMeta.icon;
  const canRetry = ch.status === 'failed' || ch.status === 'failed_final';

  return (
    <div
      data-testid={`channel-${ch.name}-${ch.status}`}
      title={
        `${meta.label} → ${statusMeta.label}` +
        (ch.recipient ? `\nRecipient: ${ch.recipient}` : '') +
        (ch.last_error ? `\nError: ${ch.last_error}` : '') +
        `\nAttempts: ${ch.attempts || 0}/${ch.max_attempts || 3}`
      }
      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border ${meta.color}`}
    >
      <Icon size={11} />
      <span className="font-medium">{meta.label}</span>
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ${statusMeta.color}`}>
        <StatusIcon size={9} className={statusMeta.spin ? 'animate-spin' : ''} />
        <span className="font-semibold">{statusMeta.label}</span>
      </span>
      {canRetry && (
        <button
          onClick={() => onRetry(notifId, ch.name)}
          data-testid={`retry-${notifId}-${ch.name}`}
          className="ml-0.5 text-rose-600 hover:text-rose-800"
          title="Retry"
        >
          <RefreshCw size={11} />
        </button>
      )}
    </div>
  );
}


// ─── Delivery matrix ─────────────────────────────────────────────────────


function DeliveryMatrix({ stats }) {
  const byChannel = useMemo(() => stats?.by_channel || {}, [stats]);
  const channels = ['in_app', 'email', 'sms', 'whatsapp'];
  const statuses = ['delivered', 'sent', 'queued', 'sending', 'retrying', 'failed', 'failed_final'];

  return (
    <div data-testid="delivery-matrix" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center gap-2">
        <Activity size={16} className="text-indigo-600" />
        <h2 className="text-sm font-semibold text-slate-900">Channel × Status (last 30 days)</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Channel</th>
              {statuses.map((s) => (
                <th key={s} className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {STATUS_META[s]?.label || s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {channels.map((c) => {
              const row = byChannel[c] || {};
              const total = Object.values(row).reduce((a, b) => a + (b || 0), 0);
              const meta = CHANNEL_META[c];
              const Icon = meta.icon;
              return (
                <tr key={c} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-900 flex items-center gap-2">
                    <Icon size={14} className="text-slate-500" />
                    {meta.label}
                    <span className="text-xs text-slate-400 ml-1">({total})</span>
                  </td>
                  {statuses.map((s) => {
                    const count = row[s] || 0;
                    if (count === 0) {
                      return <td key={s} className="px-3 py-2 text-right text-slate-300">—</td>;
                    }
                    const cls = (STATUS_META[s] || {}).color || 'bg-slate-100';
                    return (
                      <td key={s} className="px-3 py-2 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
                          {count}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {stats && (
        <div className="px-5 py-3 border-t border-slate-100 flex flex-wrap gap-3 text-xs text-slate-600">
          {Object.entries(stats.by_dispatch_status || {}).map(([k, v]) => {
            if (!k || k === 'null') return null;
            return (
              <span key={k} className={`px-2 py-0.5 rounded-full border font-semibold capitalize ${DISPATCH_BADGE[k] || 'bg-slate-100 border-slate-200'}`}>
                {k}: {v}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Test engine panel ───────────────────────────────────────────────────


function TestEnginePanel({ onSent }) {
  const [form, setForm] = useState({
    title: 'Test notification',
    message: 'End-to-end multi-channel delivery test from the engine.',
    severity: 'info',
    email_to: '',
    sms_to: '',
    wa_to: '',
  });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    const channels = [{ name: 'in_app' }];
    if (form.email_to) channels.push({ name: 'email', recipient: form.email_to });
    if (form.sms_to)   channels.push({ name: 'sms', recipient: form.sms_to });
    if (form.wa_to)    channels.push({ name: 'whatsapp', recipient: form.wa_to });

    setSending(true);
    try {
      const { data } = await axiosInstance.post('/notifications/send-test', {
        title: form.title,
        message: form.message,
        severity: form.severity,
        channels,
      });
      setLastResult({ ok: true, ...data });
      toast.success(`Notification queued · ${data.channels?.join(', ')}`);
      onSent && onSent();
    } catch (err) {
      setLastResult({ ok: false, error: err.response?.data?.detail || err.message });
      toast.error('Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="test-engine-panel" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Beaker size={16} className="text-indigo-600" />
        <h2 className="text-sm font-semibold text-slate-900">Send a test notification</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
          email · sms · whatsapp are mocked
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Title</label>
          <Input
            data-testid="test-title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Severity</label>
          <select
            data-testid="test-severity"
            value={form.severity}
            onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
            className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
          >
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Message</label>
          <Input
            data-testid="test-message"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center gap-1.5">
            <Mail size={11} /> Email recipient (optional)
          </label>
          <Input
            data-testid="test-email-to"
            placeholder="cfo@company.com"
            value={form.email_to}
            onChange={(e) => setForm((f) => ({ ...f, email_to: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center gap-1.5">
            <Smartphone size={11} /> SMS recipient (optional)
          </label>
          <Input
            data-testid="test-sms-to"
            placeholder="+9665XXXXXXXX"
            value={form.sms_to}
            onChange={(e) => setForm((f) => ({ ...f, sms_to: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center gap-1.5">
            <MessageSquare size={11} /> WhatsApp recipient (optional)
          </label>
          <Input
            data-testid="test-wa-to"
            placeholder="+9665XXXXXXXX"
            value={form.wa_to}
            onChange={(e) => setForm((f) => ({ ...f, wa_to: e.target.value }))}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={send}
          disabled={sending}
          data-testid="test-send-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {sending
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…</>
            : <><Send size={14} className="mr-1.5" /> Send test notification</>}
        </Button>
        {lastResult && (
          <div data-testid="test-last-result" className={`text-xs ${lastResult.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
            {lastResult.ok
              ? <>Queued <code className="bg-slate-100 px-1 rounded">{lastResult.notification_id}</code> on {lastResult.channels?.join(', ')}</>
              : <>Error: {lastResult.error}</>}
          </div>
        )}
      </div>
    </div>
  );
}
